import { api } from './emptyApi'

export const addTagTypes = ['health', 'Vehicle Simulators'] as const
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: (build) => ({
      ready: build.query<ReadyResponse, ReadyArgs>({
        query: () => ({ url: `/api/vehicle-simulator/ready` }),
        providesTags: ['health']
      }),
      listVehicleSims: build.query<ListVehicleSimsResponse, ListVehicleSimsArgs>({
        query: () => ({ url: `/api/vehicle-simulator/vehicles` }),
        providesTags: ['Vehicle Simulators']
      }),
      createVehicleSim: build.mutation<CreateVehicleSimResponse, CreateVehicleSimArgs>({
        query: (queryArg) => ({
          url: `/api/vehicle-simulator/vehicles`,
          method: 'POST',
          body: queryArg.vehicleSimConfig
        }),
        invalidatesTags: ['Vehicle Simulators']
      }),
      getVehicleSim: build.query<GetVehicleSimResponse, GetVehicleSimArgs>({
        query: (queryArg) => ({ url: `/api/vehicle-simulator/vehicles/${queryArg.vehicleId}` }),
        providesTags: ['Vehicle Simulators']
      }),
      updateVehicleSim: build.mutation<UpdateVehicleSimResponse, UpdateVehicleSimArgs>({
        query: (queryArg) => ({
          url: `/api/vehicle-simulator/vehicles/${queryArg.vehicleId}`,
          method: 'PUT',
          body: queryArg.vehicleSimConfig
        }),
        invalidatesTags: ['Vehicle Simulators']
      }),
      deleteVehicleSim: build.mutation<DeleteVehicleSimResponse, DeleteVehicleSimArgs>({
        query: (queryArg) => ({ url: `/api/vehicle-simulator/vehicles/${queryArg.vehicleId}`, method: 'DELETE' }),
        invalidatesTags: ['Vehicle Simulators']
      }),
      sendVehicleSimCommand: build.mutation<SendVehicleSimCommandResponse, SendVehicleSimCommandArgs>({
        query: (queryArg) => ({
          url: `/api/vehicle-simulator/vehicles/${queryArg.vehicleId}/commands`,
          method: 'POST',
          body: queryArg.body
        }),
        invalidatesTags: ['Vehicle Simulators']
      })
    }),
    overrideExisting: false
  })
export { injectedRtkApi as enhancedApi }
export type ReadyResponse = /** status 200 Successful Response */ ReadinessResponse
export type ReadyArgs = void
export type ListVehicleSimsResponse = /** status 200 Successful Response */ VehicleSimSummary[]
export type ListVehicleSimsArgs = void
export type CreateVehicleSimResponse = /** status 201 Successful Response */ VehicleSimSummary
export type CreateVehicleSimArgs = {
  vehicleSimConfig: VehicleSimConfig
}
export type GetVehicleSimResponse = /** status 200 Successful Response */ VehicleSimDetail
export type GetVehicleSimArgs = {
  vehicleId: string
}
export type UpdateVehicleSimResponse = /** status 200 Successful Response */ VehicleSimSummary
export type UpdateVehicleSimArgs = {
  vehicleId: string
  vehicleSimConfig: VehicleSimConfig
}
export type DeleteVehicleSimResponse = unknown
export type DeleteVehicleSimArgs = {
  vehicleId: string
}
export type SendVehicleSimCommandResponse = /** status 200 Successful Response */ VehicleSimCommandResult
export type SendVehicleSimCommandArgs = {
  vehicleId: string
  body:
    | ({
        action: 'set_soc'
      } & VehicleSimSetSocCommand)
    | ({
        action: 'plug_in'
      } & VehicleSimPlugInCommand)
    | ({
        action: 'unplug'
      } & VehicleSimUnplugCommand)
    | ({
        action: 'drive'
      } & VehicleSimDriveCommand)
    | ({
        action: 'go_idle'
      } & VehicleSimGoIdleCommand)
    | ({
        action: 'stop_charging'
      } & VehicleSimStopChargingCommand)
    | ({
        action: 'sync_schedule'
      } & VehicleSimSyncScheduleCommand)
}
export type ReadinessResponse = {
  status: string
  vehicles_configured: number
  vehicles_active: number
}
export type VehicleSimSummary = {
  vehicle_id: string
  state: string
  soc: number
  auth_tag: string | null
  transaction_id: number | null
}
export type ValidationError = {
  loc: (string | number)[]
  msg: string
  type: string
  ctx?: object
}
export type HttpValidationError = {
  detail?: ValidationError[]
}
export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'
export type SimScheduleWindow = {
  /** HH:MM format, 24-hour clock */
  start_time: string
  /** HH:MM format, 24-hour clock */
  end_time: string
  /** 'drive' or 'charge' */
  activity: string
  skip_probability?: number
  /** Random +/- offset applied to start/end times daily (minutes) */
  jitter_minutes?: number
}
export type DailySimSchedule = {
  day: DayOfWeek
  windows: SimScheduleWindow[]
}
export type ChargerSimMapping = {
  /** Charger identifier, e.g. ARCTESTCHRG1 */
  charger_id: string
  connector_id?: number
}
export type SimLocationConfig = {
  latitude: number
  longitude: number
}
export type SimScheduleSource = 'manual' | 'synced' | 'duty_cycle'
export type SimDutyCycleSettings = {
  /** Lower bound of the SoC band that triggers a return to the site to charge */
  return_soc_min?: number
  /** Upper bound of the SoC band that triggers a return to the site to charge */
  return_soc_max?: number
  /** Lower bound of the SoC band at which charging stops and the vehicle departs */
  depart_soc_min?: number
  /** Upper bound of the SoC band at which charging stops and the vehicle departs */
  depart_soc_max?: number
  /** Lower bound of the random multiplier applied to the vehicle's drive consumption rate */
  consumption_factor_min?: number
  /** Upper bound of the random multiplier applied to the vehicle's drive consumption rate */
  consumption_factor_max?: number
  /** Maximum random dwell after charging before the vehicle departs (minutes) */
  post_charge_dwell_minutes_max?: number
}
export type SimSyncedScheduleSegment = {
  /** Segment start (UTC, tz-aware) */
  start: string
  /** Segment end (UTC, tz-aware) */
  end: string
  activity: 'drive' | 'charge'
  /** Which platform construct produced this segment */
  source: 'trip' | 'charger_event' | 'auto_charge' | 'optimization'
  /** Platform record id (trip / charger-event) for traceability */
  source_id?: string | null
  /** Target SoC from the source record */
  target_soc?: number | null
  /** Trip energy requirement (kWh), if known */
  energy_kwh?: number | null
  /** Per-hour energy draw for a drive segment, derived from the trip's energy requirement spread linearly over its full departure->arrival duration. When set, the agent drains at this rate so the sim consumes the energy the optimizer planned for the trip; None (no energy requirement) leaves the vehicle's static configured rate in charge. */
  drive_consumption_kwh_per_hour?: number | null
  /** Charge powerOffered (kW), if known */
  power_kw?: number | null
  /** Charger (OCPP identity, e.g. ARCTESTCHRG1) the source assigned this charge window to; when set the agent plugs in here instead of the vehicle's default depot charger */
  assigned_charger_id?: string | null
  /** Connector (1-based port) on the assigned charger, if known */
  assigned_connector_id?: number | null
}
export type SimSyncSettings = {
  /** Days of schedule to keep populated ahead of now */
  horizon_days?: number
  /** Background re-sync cadence in hours */
  sync_interval_hours?: number
  /** Include the platform's Scheduled Charges (Charger Events) as charge segments */
  use_explicit_charger_events?: boolean
  /** Use the EMS planner's VehicleOptimization charge windows to place charge segments (more faithful than full-gap charging, e.g. under connector sharing) */
  use_vehicle_optimizations?: boolean
  /** Fallback only: when there are no VehicleOptimizations, fill each gap between consecutive trips with a charge segment so SoC recovers */
  auto_charge_between_trips?: boolean
}
export type VehicleSimConfig = {
  /** ID matching the fleet management platform */
  vehicle_id: string
  battery_capacity_kwh?: number
  initial_soc?: number
  /** Energy consumed per hour of driving */
  drive_consumption_kwh_per_hour?: number
  /** Energy consumed per km of driving (used to derive trip distance) */
  efficiency_kwh_per_km?: number
  idle_drain_pct_per_hour?: number
  schedule?: DailySimSchedule[]
  /** IANA timezone the schedule's days and times are expressed in (e.g. America/Los_Angeles). Schedules are evaluated against the current time in this zone, so windows behave correctly across DST changes and UTC day boundaries. */
  schedule_timezone?: string
  charger: ChargerSimMapping
  depot_location?: SimLocationConfig | null
  telemetry_interval_seconds?: number | null
  schedule_source?: SimScheduleSource
  /** Platform site the vehicle charges at in duty-cycle mode; determines which chargers it may plug into. Resolved from the vehicle's home site when unset. */
  site_id?: string | null
  duty_cycle_settings?: SimDutyCycleSettings
  /** Concrete dated segments produced by the last platform sync */
  synced_segments?: SimSyncedScheduleSegment[]
  /** When synced_segments were last refreshed (UTC) */
  last_synced_at?: string | null
  sync_settings?: SimSyncSettings
}
export type VehicleSimConfigResponse = {
  /** ID matching the fleet management platform */
  vehicle_id: string
  battery_capacity_kwh: number
  initial_soc: number
  drive_consumption_kwh_per_hour: number
  efficiency_kwh_per_km: number
  idle_drain_pct_per_hour: number
  schedule?: DailySimSchedule[]
  /** IANA timezone the schedule's days and times are expressed in */
  schedule_timezone?: string
  charger: ChargerSimMapping
  depot_location?: SimLocationConfig | null
  telemetry_interval_seconds?: number | null
  schedule_source?: SimScheduleSource
  /** Platform site the vehicle charges at in duty-cycle mode; determines which chargers it may plug into. Resolved from the vehicle's home site when unset. */
  site_id?: string | null
  duty_cycle_settings?: SimDutyCycleSettings
  synced_segments?: SimSyncedScheduleSegment[]
  last_synced_at?: string | null
  sync_settings?: SimSyncSettings
  /** Normalized auth tag resolved from the platform (used as OCPP idTag) */
  auth_tag?: string | null
}
export type VehicleSimDetail = {
  vehicle_id: string
  state: string
  soc: number
  auth_tag: string | null
  transaction_id: number | null
  /** True while a manual action holds the state; the schedule resumes control at its next window boundary */
  manual_hold: boolean
  active_charger_id: string
  active_connector_id: number
  config: VehicleSimConfigResponse
}
export type VehicleSimScheduleSyncResult = {
  trips_fetched: number
  charges_fetched: number
  optimizations_fetched: number
  segments_created: number
  last_synced_at?: string | null
  warnings?: string[]
}
export type VehicleSimCommandResult = {
  vehicle_id: string
  state: string
  soc: number
  auth_tag: string | null
  transaction_id: number | null
  /** plug_in only: whether the charger authorized a session */
  charging_started?: boolean | null
  /** sync_schedule only: counts + warnings from the platform sync */
  sync_result?: VehicleSimScheduleSyncResult | null
}
export type VehicleSimSetSocCommand = {
  action: 'set_soc'
  soc: number
}
export type VehicleSimPlugInCommand = {
  action: 'plug_in'
  /** Defaults to the vehicle's configured charger */
  charger_id?: string | null
  connector_id?: number | null
}
export type VehicleSimUnplugCommand = {
  action: 'unplug'
}
export type VehicleSimDriveCommand = {
  action: 'drive'
  /** Omit for an open-ended drive */
  duration_minutes?: number | null
}
export type VehicleSimGoIdleCommand = {
  action: 'go_idle'
}
export type VehicleSimStopChargingCommand = {
  action: 'stop_charging'
}
export type VehicleSimSyncScheduleCommand = {
  action: 'sync_schedule'
}
export const {
  useReadyQuery,
  useLazyReadyQuery,
  useListVehicleSimsQuery,
  useLazyListVehicleSimsQuery,
  useCreateVehicleSimMutation,
  useGetVehicleSimQuery,
  useLazyGetVehicleSimQuery,
  useUpdateVehicleSimMutation,
  useDeleteVehicleSimMutation,
  useSendVehicleSimCommandMutation
} = injectedRtkApi
